import { InputHTMLAttributes, forwardRef } from "react";
import styled from "styled-components";

import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { focusOutline } from "@pages/AdminDashboard/theme/focusOutline";
import { useFieldId } from "@pages/AdminDashboard/ui/FieldFrame";

const { color, fieldTone, radius, typeScale } = adminTokens;
const [, , spaceMd] = adminTokens.spacing;
// The design's prescription card: parts 10px apart, 10px above and below.
const partGap = 10;
const verticalPadding = 10;
const boxSize = 16;
const boxRadius = 4;
const detailFontSize = typeScale.meta.fontSize;
// On a phone a card is at least 52px tall (PHONE-R37).
const phoneCardMinHeight = 52;

const Wrapper = styled.label`
  position: relative;
  display: block;
`;

// The real checkbox lies, invisible, over the whole card, so a click anywhere on
// the card toggles it and the keyboard and form behavior stay native.
const Input = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

const Detail = styled.span`
  color: ${color.inkSoft};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${detailFontSize};
`;

const Surface = styled.span<{ $isPhone: boolean }>`
  display: flex;
  align-items: center;
  gap: ${partGap}px;
  box-sizing: border-box;
  min-height: ${({ $isPhone }) => ($isPhone ? phoneCardMinHeight : 0)}px;
  padding: ${verticalPadding}px ${spaceMd}px;
  border: 1px solid ${color.border};
  border-radius: ${radius.row}px;
  background-color: ${color.surface};
  color: ${color.navy};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};

  ${Input}:checked + & {
    background-color: ${color.canvas};
    border-color: ${color.periwinkle};
  }

  ${Input}:focus-visible + & {
    ${focusOutline}
  }

  ${Input}:disabled + & {
    color: ${color.disabledText};
  }

  ${Input}:disabled + & ${Detail} {
    color: ${color.disabledText};
  }
`;

const Box = styled.span`
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${boxSize}px;
  height: ${boxSize}px;
  border: 1.5px solid ${fieldTone.markerMuted};
  border-radius: ${boxRadius}px;
  background-color: ${color.surface};

  svg {
    visibility: hidden;
  }

  ${Input}:checked + ${Surface} > & {
    background-color: ${color.tealDark};
    border-color: ${color.tealDark};
  }

  ${Input}:checked + ${Surface} > & svg {
    visibility: visible;
  }
`;

const Text = styled.span`
  flex: 1;
  min-width: 0;
`;

const Description = styled.span`
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
`;

const Title = styled.span`
  font-family: ${typeScale.medication.fontFamily};
  font-style: ${typeScale.medication.fontStyle};
  font-size: ${typeScale.medication.fontSize};
`;

export interface CheckboxCardProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "title"> {
  // The name in Medium italic, such as a medication.
  title: string;
  // The text that follows the title, such as a strength.
  description?: string;
  // What belongs at the right of the card, such as a quantity and the days.
  detail?: string;
}

// A selectable row card, as the prescription checkboxes of the Replace dialog
// (CHECKCARD-R1…R9). It hands its ref, name, change and blur to the native
// checkbox, so a react-hook-form `register` works on it unchanged.
export const CheckboxCard = forwardRef<HTMLInputElement, CheckboxCardProps>(
  ({ title, description, detail, ...inputProps }, ref) => {
    const isPhone = useIsPhoneLayout();
    const baseId = useFieldId();
    const textId = `${baseId}-text`;
    const detailId = `${baseId}-detail`;

    return (
      <Wrapper>
        <Input
          {...inputProps}
          ref={ref}
          type="checkbox"
          aria-labelledby={textId}
          aria-describedby={detail ? detailId : undefined}
        />
        <Surface $isPhone={isPhone}>
          <Box>
            <svg
              aria-hidden="true"
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke={color.surface}
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m5 12 5 5 9-10" />
            </svg>
          </Box>
          <Text id={textId}>
            <Title>{title}</Title>
            {description && (
              <>
                {" "}
                <Description>{description}</Description>
              </>
            )}
          </Text>
          {detail && <Detail id={detailId}>{detail}</Detail>}
        </Surface>
      </Wrapper>
    );
  }
);

CheckboxCard.displayName = "CheckboxCard";
